import CurrentRiskBadge from '../../components/CurrentRiskBadge';
import { actionCounts } from '../../utils/actionCounts';
import { isTaskOverdue } from '../../utils/taskPresentation';
import usePageTitle from '../../hooks/usePageTitle';
import React, { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  CheckSquare,
  AlertTriangle,
  Clock,
  CheckCircle,
  ArrowRight,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import { Task, Facility } from '../../types';
import { tasksService, facilitiesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalScope } from '../../hooks/usePortalScope';
import { formatShortDate } from '../../utils/dateUtils';
import './ClientPortalDashboard.css';

const ClientPortalDashboard: React.FC = () => {
  usePageTitle('Dashboard');
  const { currentUser, loading: authLoading } = useAuth();
  const { clientId, managerId, scopeFilter } = usePortalScope();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!scopeFilter) {
      setTasks([]);
      setFacilities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [tasksData, facilitiesData] = await Promise.all([
        tasksService.getTasks(scopeFilter),
        facilitiesService.getFacilities(scopeFilter),
      ]);

      setTasks(tasksData.filter(task => !task.parent_task_id));
      setFacilities(facilitiesData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, managerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);

  const counts = actionCounts(tasks);
  // Calculate statistics
  const overdueActions = tasks.filter(
    (t) => isTaskOverdue(t)
  );

  // Get recent actions (last 5)
  const recentActions = tasks.filter(task => task.status !== 'cancelled')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="loading-spinner" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!clientId && !managerId) {
    return (
      <div className="client-dashboard-loading">
        <p>No account scope found. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div data-tour="portal-dashboard" className="client-portal-dashboard">
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Welcome back{currentUser?.name ? `, ${currentUser.name}` : ''}!</h1>
          <p>Here's an overview of your water management activities.</p>
        </div>
        <div className="header-actions">
          <Link to="/portal/actions" className="btn btn-primary">
            <CheckSquare size={18} />
            View All Actions
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card stat-facilities">
          <div className="stat-icon">
            <Building2 size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{facilities.length}</div>
            <div className="stat-label">Facilities</div>
          </div>
        </div>

        <div className="stat-card stat-pending">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{counts.open}</div>
            <div className="stat-label">Pending Actions</div>
          </div>
        </div>

        <div className="stat-card stat-overdue">
          <div className="stat-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{overdueActions.length}</div>
            <div className="stat-label">Overdue</div>
          </div>
        </div>

        <div className="stat-card stat-complete">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{counts.complete}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      </div>

      {/* Overdue Actions Alert */}
      {overdueActions.length > 0 && (
        <div className="alert-banner alert-warning">
          <AlertTriangle size={20} />
          <div className="alert-content">
            <strong>Attention Required:</strong> {overdueActions.length} overdue action{overdueActions.length === 1 ? '' : 's'} across your facilities ({overdueActions.filter(t => t.responsible_user_id === currentUser?.id).length} assigned to you)
          </div>
          <Link to="/portal/actions?filter=overdue" className="alert-action">
            View Overdue
            <ArrowRight size={16} />
          </Link>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Recent Actions */}
        <div className="dashboard-card actions-card">
          <div className="card-header">
            <h2>
              <CheckSquare size={20} />
              Recent Actions
            </h2>
            <Link to="/portal/actions" className="view-all-link">
              View All <ArrowRight size={16} />
            </Link>
          </div>
          <div className="card-content">
            {recentActions.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={32} />
                <p>No actions assigned yet.</p>
              </div>
            ) : (
              <ul className="actions-list">
                {recentActions.map((action) => {
                  const isOverdue =
                    action.due_by &&
                    new Date(action.due_by) < new Date() &&
                    action.status !== 'complete';

                  return (
                    <li key={action.id} className={`action-item ${isOverdue ? 'overdue' : ''}`}>
                      <div className="action-status">
                        {action.status === 'complete' ? (
                          <CheckCircle size={16} className="status-complete" />
                        ) : isOverdue ? (
                          <AlertTriangle size={16} className="status-overdue" />
                        ) : (
                          <Clock size={16} className="status-pending" />
                        )}
                      </div>
                      <div className="action-details">
                        <div className="action-description">{action.description}</div>
                        {action.due_by && (
                          <div className="action-due">
                            <Calendar size={12} />
                            Due: {formatShortDate(action.due_by)}
                          </div>
                        )}
                      </div>
                      <div className={`action-badge ${action.status}`}>
                        {action.status === 'not_started'
                          ? 'Not Started'
                          : action.status === 'acknowledged'
                          ? 'Acknowledged'
                          : action.status === 'started'
                          ? 'In Progress'
                          : action.status === 'blocked'
                          ? 'Blocked'
                          : action.status === 'complete'
                          ? 'Complete'
                          : action.status}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Facilities Overview */}
        <div className="dashboard-card facilities-card">
          <div className="card-header">
            <h2>
              <Building2 size={20} />
              Your Facilities
            </h2>
            <Link to="/portal/facilities" className="view-all-link">
              View All <ArrowRight size={16} />
            </Link>
          </div>
          <div className="card-content">
            {facilities.length === 0 ? (
              <div className="empty-state">
                <Building2 size={32} />
                <p>No facilities assigned yet.</p>
              </div>
            ) : (
              <ul className="facilities-list">
                {facilities.slice(0, 4).map((facility) => (
                  <li key={facility.id} className="facility-item">
                    <div className="facility-icon">
                      <Building2 size={20} />
                    </div>
                    <div className="facility-details">
                      <div className="facility-name">{facility.name}</div><CurrentRiskBadge risk={facility.risk_summary?.current_risk} />
                      <div className="facility-address">{facility.address}</div>
                    </div>
                    <Link to={`/portal/facilities/${facility.id}`} className="facility-link">
                      <ArrowRight size={16} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card quick-actions-card">
          <div className="card-header">
            <h2>
              <TrendingUp size={20} />
              Quick Actions
            </h2>
          </div>
          <div className="card-content">
            <div className="quick-actions-grid">
              <Link to="/portal/actions" className="quick-action-btn">
                <CheckSquare size={24} />
                <span>View Actions</span>
              </Link>
              <Link to="/portal/facilities" className="quick-action-btn">
                <Building2 size={24} />
                <span>View Facilities</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPortalDashboard;
